import { useState } from "react";
import {
  emailLooksValid,
  RESET_EMAIL_INVALID,
  RESET_UNREACHABLE,
} from "../../lib/passwordPolicy";
import { DialogHeader, Popup } from "../../ui/Popup";

const fieldClass =
  "w-full rounded border border-line bg-canvas px-3 py-2 text-sm text-fg outline-none focus:border-muted";

export function resetSentSentence(email: string): string {
  return `If an account exists for ${email}, we've sent a link to reset your password. It expires in 1 hour. Check your spam folder if you don't see it.`;
}

export function ResetPasswordDialog({
  email: initialEmail = "",
  onClose,
  onBack,
  onSend,
}: {
  email?: string;
  onClose: () => void;
  onBack?: () => void;
  onSend?: (email: string) => Promise<void>;
}) {
  const [email, setEmail] = useState(initialEmail);
  const [problem, setProblem] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const back = onBack ?? onClose;

  const send = async () => {
    if (!onSend) return;
    const trimmed = email.trim();
    if (!emailLooksValid(trimmed)) {
      setProblem(RESET_EMAIL_INVALID);
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      await onSend(trimmed);
      setSentTo(trimmed);
    } catch {
      setProblem(RESET_UNREACHABLE);
      setBusy(false);
    }
  };

  return (
    <Popup
      onClose={onClose}
      className="flex max-h-[85vh] w-full max-w-sm flex-col rounded-lg bg-surface text-fg shadow-2xl"
    >
      <DialogHeader title="Reset your password" onClose={onClose} />
      <div className="space-y-3 p-4">
        {sentTo ? (
          <p className="text-xs text-muted">{resetSentBody(sentTo)}</p>
        ) : (
          <ResetForm
            email={email}
            problem={problem}
            busy={busy}
            canSend={!!onSend}
            onEmail={setEmail}
            onSend={() => void send()}
            onBack={back}
          />
        )}
      </div>
    </Popup>
  );
}

function resetSentBody(email: string) {
  const sentence = resetSentSentence(email);
  const at = sentence.indexOf(email);
  return (
    <>
      {sentence.slice(0, at)}
      <strong>{email}</strong>
      {sentence.slice(at + email.length)}
    </>
  );
}

function ResetForm({
  email,
  problem,
  busy,
  canSend,
  onEmail,
  onSend,
  onBack,
}: {
  email: string;
  problem: string | null;
  busy: boolean;
  canSend: boolean;
  onEmail: (value: string) => void;
  onSend: () => void;
  onBack: () => void;
}) {
  return (
    <form
      className="space-y-3"
      noValidate
      onSubmit={(event) => {
        // The dialog is portaled, but React bubbles submit through the
        // component tree, so the sign-in form around the link would submit too.
        event.preventDefault();
        event.stopPropagation();
        onSend();
      }}
    >
      <p className="text-xs text-muted">
        Enter the email you use for VERO. We'll send you a link to set a new
        password.
      </p>
      <label className="block space-y-1 text-xs text-muted">
        Email
        <input
          className={fieldClass}
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => onEmail(event.target.value)}
        />
      </label>
      {problem && <p className="text-xs text-red-400">{problem}</p>}
      <button
        type="submit"
        disabled={!canSend || busy}
        className="w-full rounded bg-accent px-3 py-2 text-sm font-medium text-canvas hover:bg-accent-hover disabled:opacity-60"
      >
        Send reset link
      </button>
      <button
        type="button"
        className="w-full text-center text-xs text-muted hover:text-fg"
        onClick={onBack}
      >
        Back to sign in
      </button>
    </form>
  );
}
